/**
 * Google Analytics with consent management and IP anonymization
 * GDPR/CCPA compliant implementation
 */

import { hasConsent } from './consentManager';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-7Q0K4C8BBW';

/**
 * Initialize Google Analytics with privacy-friendly settings
 * Only called when user has given analytics consent
 */
export const initializeGoogleAnalytics = (): void => {
  // Check consent before initializing
  if (!hasConsent('analytics')) {
    console.log('Google Analytics initialization skipped - no consent');
    return;
  }

  try {
    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Set timestamp
    window.gtag('js', new Date());

    // Configure GA with privacy settings
    window.gtag('config', GA_MEASUREMENT_ID, {
      'anonymize_ip': true, // IP anonymization (GDPR requirement)
      'allow_google_signals': hasConsent('marketing'), // Only if marketing consent given
      'allow_ad_personalization_signals': hasConsent('marketing'), // Only if marketing consent given
      'cookie_flags': 'SameSite=None;Secure', // Enhanced cookie security
    });

    // Load GA script dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    console.log('Google Analytics initialized with IP anonymization');
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Disable Google Analytics (called when consent is revoked)
 */
export const disableGoogleAnalytics = (): void => {
  try {
    // Set the GA disable flag
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

    console.log('Google Analytics disabled');
  } catch (error) {
    console.error('Error disabling Google Analytics:', error);
  }
};

/**
 * Track a custom event in Google Analytics
 * Automatically checks consent before tracking
 */
export const trackGAEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (!hasConsent('analytics')) {
    console.log('GA event tracking skipped - no consent');
    return;
  }

  try {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, eventParams);
    }
  } catch (error) {
    console.error('Error tracking GA event:', error);
  }
};

/**
 * Track a page view in Google Analytics
 * Automatically checks consent before tracking
 */
export const trackGAPageView = (
  pageTitle: string,
  pagePath?: string,
  additionalParams?: Record<string, any>
): void => {
  if (!hasConsent('analytics')) {
    console.log('GA page view tracking skipped - no consent');
    return;
  }

  try {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_path: pagePath || window.location.pathname,
        page_location: window.location.href,
        ...additionalParams
      });
    }
  } catch (error) {
    console.error('Error tracking GA page view:', error);
  }
};

/**
 * Update Google Analytics consent state
 * Called when user changes consent preferences
 */
export const updateGAConsent = (hasAnalyticsConsent: boolean, hasMarketingConsent: boolean): void => {
  try {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': hasAnalyticsConsent ? 'granted' : 'denied',
        'ad_storage': hasMarketingConsent ? 'granted' : 'denied',
        'ad_user_data': hasMarketingConsent ? 'granted' : 'denied',
        'ad_personalization': hasMarketingConsent ? 'granted' : 'denied',
      });
    }
  } catch (error) {
    console.error('Error updating GA consent:', error);
  }
};

export default {
  initialize: initializeGoogleAnalytics,
  disable: disableGoogleAnalytics,
  trackEvent: trackGAEvent,
  trackPageView: trackGAPageView,
  updateConsent: updateGAConsent
};
